import React, { useCallback, useRef } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

type InputNumberBaseProps = {
  variant?: "primary" | "secondary" | "danger";
  density?: "compact" | "default" | "spacious";
  borderRadius?: "sm" | "base" | "lg";
  hasError?: boolean;
  className?: string;
};

export type InputNumberProps = InputNumberBaseProps &
  Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    "type" | "className"
  >;

function parseBound(value: string | number | undefined): number | undefined {
  if (value === undefined || value === "") return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function parseStep(step: string | number | undefined): number {
  if (step === undefined || step === "any") return 1;
  const parsed = Number(step);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
}

function clampValue(
  value: number,
  min: number | undefined,
  max: number | undefined,
): number {
  let next = value;
  if (min !== undefined) next = Math.max(min, next);
  if (max !== undefined) next = Math.min(max, next);
  return next;
}

function createSyntheticChangeEvent(
  input: HTMLInputElement,
  nextValue: string,
): React.ChangeEvent<HTMLInputElement> {
  return {
    target: { ...input, value: nextValue },
    currentTarget: input,
  } as React.ChangeEvent<HTMLInputElement>;
}

/**
 * Number input with custom stepper buttons and hidden native spinners.
 * @component
 */
export function InputNumber({
  className = "",
  variant = "primary",
  density = "default",
  borderRadius = "sm",
  hasError = false,
  min,
  max,
  step = 1,
  value,
  disabled = false,
  onChange,
  onBlur,
  ...rest
}: InputNumberProps): React.ReactElement {
  const inputRef = useRef<HTMLInputElement>(null);

  const shellClassName = [
    "input-number",
    `input-number--${variant}`,
    `input-number--${density}`,
    borderRadius !== "sm" && `input-number--radius-${borderRadius}`,
    hasError && "input-number--error",
    disabled && "input-number--disabled",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const inputClassName = [
    "input-number__field",
    `input-number__field--${density}`,
  ]
    .filter(Boolean)
    .join(" ");

  const applyValue = useCallback(
    (raw: string) => {
      const input = inputRef.current;
      if (!input || !onChange) return;
      onChange(createSyntheticChangeEvent(input, raw));
    },
    [onChange],
  );

  const stepBy = useCallback(
    (direction: 1 | -1) => {
      const input = inputRef.current;
      if (!input || disabled) return;

      const minBound = parseBound(min);
      const maxBound = parseBound(max);
      const stepAmount = parseStep(step);
      const current = Number(input.value);
      const base = Number.isFinite(current) ? current : minBound ?? 0;
      const next = clampValue(base + direction * stepAmount, minBound, maxBound);
      applyValue(String(next));
    },
    [applyValue, disabled, max, min, step],
  );

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const minBound = parseBound(min);
    const maxBound = parseBound(max);
    const raw = event.target.value.trim();

    if (raw === "" || Number.isNaN(Number(raw))) {
      const fallback =
        minBound !== undefined ? String(minBound) : "0";
      applyValue(fallback);
    } else {
      const normalized = String(
        clampValue(Number(raw), minBound, maxBound),
      );
      if (normalized !== raw) {
        applyValue(normalized);
      }
    }

    onBlur?.(event);
  };

  return (
    <div className={shellClassName}>
      <input
        {...rest}
        ref={inputRef}
        type="number"
        className={inputClassName}
        min={min}
        max={max}
        step={step}
        value={value}
        disabled={disabled}
        onChange={onChange}
        onBlur={handleBlur}
      />
      <div className="input-number__steppers">
        <button
          type="button"
          className="input-number__stepper input-number__stepper--increment"
          disabled={disabled}
          tabIndex={-1}
          aria-label="Aumentar valor"
          onClick={() => stepBy(1)}
        >
          <ChevronUp className="input-number__stepper-icon" aria-hidden />
        </button>
        <button
          type="button"
          className="input-number__stepper input-number__stepper--decrement"
          disabled={disabled}
          tabIndex={-1}
          aria-label="Disminuir valor"
          onClick={() => stepBy(-1)}
        >
          <ChevronDown className="input-number__stepper-icon" aria-hidden />
        </button>
      </div>
    </div>
  );
}

export default InputNumber;
